import { useState, useEffect, useCallback, useRef } from "react";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ComposedChart,
} from "recharts";
import { Fetchexecuteclquery } from "../../../services/process/BRS1/brsService7";
import axios from "axios";

/* ═══════════════════════════════════════════════
   DESIGN TOKENS — Refined Enterprise Light Mode
═══════════════════════════════════════════════ */
// AFTER
const C = {
  bg:         "#EEF1F6",       // light blue-grey page background
  surface:    "#FFFFFF",
  surfaceAlt: "#F4F6FB",       // chart card header background
  border:     "#D8DCE6",
  borderMed:  "#C4CBDA",
  navy:       "#0F2044",       // header, role tabs background
  navyMid:    "#1A3060",       // primary chart bar colour
  blue:       "#2E4FA3",       // secondary chart colour
  blueSoft:   "#EEF2FF",
  blueLight:  "#C7D2FE",
  teal:       "#5BBFA3",       // tertiary chart colour (replaces teal)
  tealSoft:   "#E8F7F3",
  amber:      "#E05A2B",       // accent / KPI left-border highlight
  amberSoft:  "#FEF0EA",
  red:        "#C0392B",
  redSoft:    "#FDECEA",
  green:      "#27825A",
  greenSoft:  "#E6F5EE",
  purple:     "#7B5EA7",       // quaternary chart colour
  purpleSoft: "#F0ECF8",
  text:       "#1A2035",
  textMid:    "#3D4A66",
  textSub:    "#5A6480",
  textMut:    "#8A93AD",
  mono:       "'IBM Plex Mono', 'Cascadia Code', monospace",
  sans:       "'IBM Plex Sans', system-ui, sans-serif",
  head:       "'IBM Plex Sans', system-ui, sans-serif",
};

const CHART_COLORS = ["#1A3060", "#2E4FA3", "#7B5EA7", "#E05A2B", "#3D9BE0", "#5BBFA3", "#27825A", "#C0392B"];

/* ═══════════════════════════════════════════════
   ROLES CONFIG
═══════════════════════════════════════════════ */
const ROLES = [
  { id: "CEO",  label: "CEO / Board",          icon: "◈", color: C.navy   },
  { id: "CFO",  label: "CFO",                  icon: "◎", color: C.blue   },
  { id: "COO",  label: "COO",                  icon: "◉", color: C.teal   },
  { id: "VPS",  label: "VP Sales",             icon: "◐", color: "#7C3AED"},
  { id: "VPP",  label: "VP Procurement",       icon: "◑", color: C.amber  },
  { id: "VPH",  label: "VP HR",                icon: "◒", color: "#0891B2"},
  { id: "DH",   label: "Division Head",        icon: "△", color: "#BE185D"},
  { id: "DPH",  label: "Department Head",      icon: "▽", color: "#15803D"},
  { id: "FM",   label: "Finance Manager",      icon: "◇", color: "#9D174D"},
  { id: "CCM",  label: "Cost Center Manager",  icon: "○", color: "#065F46"},
  { id: "OPS",  label: "Operations Manager",   icon: "□", color: "#92400E"},
];

/* ═══════════════════════════════════════════════
   FINANCIAL YEARS (India fiscal: Apr-Mar)
═══════════════════════════════════════════════ */
const FIN_YEARS = [
  { id: "FY2026", label: "FY 2025-26", start: "2025-04-01", end: "2026-03-31" },
  { id: "FY2025", label: "FY 2024-25", start: "2024-04-01", end: "2025-03-31" },
  { id: "FY2024", label: "FY 2023-24", start: "2023-04-01", end: "2024-03-31" },
  { id: "FY2023", label: "FY 2022-23", start: "2022-04-01", end: "2023-03-31" },
  { id: "FY2022", label: "FY 2021-22", start: "2021-04-01", end: "2022-03-31" },
  { id: "FY2021", label: "FY 2020-21", start: "2020-04-01", end: "2021-03-31" },
  { id: "FY2020", label: "FY 2019-20", start: "2019-04-01", end: "2020-03-31" },
  { id: "FY2019", label: "FY 2018-19", start: "2018-04-01", end: "2019-03-31" },
  { id: "FY2018", label: "FY 2017-18", start: "2017-04-01", end: "2018-03-31" },
  { id: "FY2017", label: "FY 2016-17", start: "2016-04-01", end: "2017-03-31" },
  { id: "ALL",    label: "All Years",  start: "2016-04-01", end: "2026-03-31" },
];

const ENTITIES = [
  { id: "ALL", label: "All Entities" },
  { id: "1",   label: "Entity 1" },
  { id: "2",   label: "Entity 2" },
  { id: "3",   label: "Entity 3" },
  { id: "4",   label: "Entity 4" },
  { id: "5",   label: "Entity 5" },
  { id: "6",   label: "Entity 6" },
  { id: "7",   label: "Entity 7" },
  { id: "8",   label: "Entity 8" },
];

/* ═══════════════════════════════════════════════
   QUERY HELPERS
═══════════════════════════════════════════════ */
function buildEntityFilter(entity, alias) {
  if (!entity || entity === "ALL") return "";
  const col = alias ? `${alias}.entity_id` : "entity_id";
  return `AND ${col} = ${entity}`;
}
function buildDateFilter(fy, column, alias) {
  if (!fy || !fy.start || !fy.end) return "";
  const col = alias ? `${alias}.${column}` : column;
  return `AND ${col} BETWEEN '${fy.start}' AND '${fy.end}'`;
}

const FY_MONTH = (col) => `
CASE 
  WHEN toMonth(${col}) >= 4 THEN toMonth(${col}) - 3
  ELSE toMonth(${col}) + 9
END
`;

const FY_MONTH_NAME = (col) => `formatDateTime(${col}, '%b')`;

/* ═══════════════════════════════════════════════
   ROLE QUERIES
═══════════════════════════════════════════════ */
const ROLE_QUERIES = {
  CEO: (fy, entity) => [
    {
      key: "revenue_trend",
      label: "Revenue Trend (Monthly)",
      category: "Revenue & P&L",
      query: `SELECT 
        ${FY_MONTH("so.order_date")} AS fy_month_order,
        ${FY_MONTH_NAME("so.order_date")} AS month,
        sum(so.total_amount) AS revenue 
      FROM sales.sales_orders so 
      WHERE so.order_status NOT IN ('CANCELLED') 
      ${buildDateFilter(fy, "order_date")} ${buildEntityFilter(entity)}
      GROUP BY fy_month_order, month ORDER BY fy_month_order`,
      chartType: "area",
      xKey: "month", yKey: "revenue", fmt: "currency",
    },
    {
      key: "revenue_by_entity",
      label: "Revenue by Entity",
      category: "Revenue & P&L",
      query: `SELECT e.entity_name, sum(so.total_amount) AS revenue
              FROM sales.sales_orders so 
              JOIN erp_core.entities e ON so.entity_id=e.entity_id
              WHERE so.order_status NOT IN ('CANCELLED')
              ${buildDateFilter(fy, "order_date")}
              GROUP BY e.entity_name ORDER BY revenue DESC LIMIT 10`,
      chartType: "bar", xKey: "entity_name", yKey: "revenue", fmt: "currency",
    },
    {
      key: "yoy_growth",
      label: "YoY Revenue Growth",
      category: "Revenue & P&L",
      query: `SELECT toYear(order_date) AS yr, sum(total_amount) AS rev
              FROM sales.sales_orders
              WHERE order_status NOT IN ('CANCELLED') ${buildEntityFilter(entity, "sales_orders")}
              GROUP BY yr ORDER BY yr`,
      chartType: "bar", xKey: "yr", yKey: "rev", fmt: "currency",
    },
    {
      key: "balance_sheet",
      label: "Balance Sheet Summary",
      category: "Balance Sheet",
      query: `SELECT account_type, abs(sum(CASE WHEN debit_credit_indicator='D' THEN amount_local ELSE -amount_local END)) AS balance
              FROM finance.universal_journal
              WHERE account_type is not null
              ${buildDateFilter(fy, "posting_date")}
              ${buildEntityFilter(entity)}
              GROUP BY account_type`,
      chartType: "pie", xKey: "account_type", yKey: "balance", fmt: "currency",
      drillQuery: (fy, entity) => `SELECT account_type, gl_account, sum(CASE WHEN debit_credit_indicator='D' THEN amount_local ELSE -amount_local END) AS balance, count() AS entries
              FROM finance.universal_journal
              WHERE account_type is not null
              ${buildDateFilter(fy, "posting_date")}
              ${buildEntityFilter(entity)}
              GROUP BY account_type, gl_account ORDER BY account_type, abs(balance) DESC LIMIT 50`,
    },
    {
      key: "cashflow",
      label: "Operating Cashflow (Monthly)",
      category: "Cash & Liquidity",
      query: `SELECT 
        ${FY_MONTH("receipt_date")} AS fy_month_order,
        ${FY_MONTH_NAME("receipt_date")} AS month,
        sum(amount) AS inflow
      FROM finance.receipts
      WHERE 1=1 ${buildDateFilter(fy, "receipt_date")}
      ${buildEntityFilter(entity)}
      GROUP BY fy_month_order, month ORDER BY fy_month_order`,
      chartType: "area", xKey: "month", yKey: "inflow", fmt: "currency",
    },
    {
      key: "headcount",
      label: "Headcount by Entity",
      category: "Operations",
      query: `SELECT e.entity_name, count(emp.employee_id) AS headcount
              FROM erp_core.employees emp
              JOIN erp_core.entities e ON emp.entity_id=e.entity_id
              WHERE emp.status='ACTIVE' ${entity !== "ALL" ? `AND emp.entity_id = ${entity}` : ""}
              GROUP BY e.entity_name ORDER BY headcount DESC`,
      chartType: "bar", xKey: "entity_name", yKey: "headcount", fmt: "number",
    },
  ],
  CFO: (fy, entity) => [
    {
      key: "ar_aging",
      label: "AR Aging Buckets",
      category: "Accounts Receivable",
      query: `SELECT 
        sumIf(ci.invoice_amount, dateDiff('day', ci.invoice_date, today()) <= 30) AS amt_0_30,
        sumIf(ci.invoice_amount, dateDiff('day', ci.invoice_date, today()) BETWEEN 31 AND 60) AS amt_31_60,
        sumIf(ci.invoice_amount, dateDiff('day', ci.invoice_date, today()) BETWEEN 61 AND 90) AS amt_61_90,
        sumIf(ci.invoice_amount, dateDiff('day', ci.invoice_date, today()) > 90) AS amt_over_90
      FROM finance.customer_invoices ci
      LEFT JOIN finance.receipts r ON ci.invoice_id = r.invoice_id
      WHERE r.receipt_id IS NULL
      ${buildDateFilter(fy, "invoice_date", "ci")}
      ${entity !== "ALL" ? `AND ci.entity_id = ${entity}` : ""}`,
      chartType: "pie_ar",
    },
    {
      key: "dso",
      label: "DSO Trend (Monthly)",
      category: "Accounts Receivable",
      query: `SELECT 
        ${FY_MONTH("ci.invoice_date")} AS fy_month_order,
        ${FY_MONTH_NAME("ci.invoice_date")} AS month,
        avg(dateDiff('day', ci.invoice_date, coalesce(r.receipt_date, today()))) AS dso
      FROM finance.customer_invoices ci
      LEFT JOIN finance.receipts r ON ci.invoice_id=r.invoice_id
      WHERE 1=1 ${buildDateFilter(fy, "invoice_date", "ci")}
      ${entity !== "ALL" ? `AND ci.entity_id = ${entity}` : ""}
      GROUP BY fy_month_order, month ORDER BY fy_month_order`,
      chartType: "line", xKey: "month", yKey: "dso", fmt: "days",
    },
    {
      key: "ap_aging",
      label: "AP Aging Buckets",
      category: "Accounts Payable",
      query: `SELECT 
        sumIf(vi.invoice_amount, dateDiff('day', vi.invoice_date, today()) <= 30) AS amt_0_30,
        sumIf(vi.invoice_amount, dateDiff('day', vi.invoice_date, today()) BETWEEN 31 AND 60) AS amt_31_60,
        sumIf(vi.invoice_amount, dateDiff('day', vi.invoice_date, today()) BETWEEN 61 AND 90) AS amt_61_90,
        sumIf(vi.invoice_amount, dateDiff('day', vi.invoice_date, today()) > 90) AS amt_over_90
      FROM finance.vendor_invoices vi
      LEFT JOIN finance.payments p ON vi.invoice_id=p.reference_invoice
      WHERE p.payment_id IS NULL
      ${buildDateFilter(fy, "invoice_date", "vi")}
      ${entity !== "ALL" ? `AND vi.entity_id = ${entity}` : ""}`,
      chartType: "pie_ap",
    },
    {
      key: "cashflow_monthly",
      label: "Monthly Cash In vs Out",
      category: "Cash Management",
      query: `SELECT 
        ${FY_MONTH("txn_date")} AS fy_month_order,
        ${FY_MONTH_NAME("txn_date")} AS month,
        sumIf(amount, amount>0) AS cash_in,
        abs(sumIf(amount, amount<0)) AS cash_out
      FROM finance.bank_statement
      WHERE 1=1 ${buildDateFilter(fy, "txn_date")}
      ${buildEntityFilter(entity)}
      GROUP BY fy_month_order, month ORDER BY fy_month_order`,
      chartType: "composed", xKey: "month", yKeys: ["cash_in","cash_out"], fmt: "currency",
    },
    {
      key: "cost_by_division",
      label: "Cost by Division",
      category: "Cost Analysis",
      query: `SELECT d.org_name AS division, sum(uj.amount_local) AS cost
              FROM finance.universal_journal uj
              JOIN erp_core.division d ON uj.entity_id=d.entity_id
              WHERE uj.account_type IN ('Expense')
              ${buildDateFilter(fy, "posting_date", "uj")}
              ${entity !== "ALL" ? `AND uj.entity_id = ${entity}` : ""}
              GROUP BY d.org_name ORDER BY cost DESC LIMIT 10`,
      chartType: "bar", xKey: "division", yKey: "cost", fmt: "currency",
    },
    {
      key: "dpo",
      label: "DPO Trend",
      category: "Accounts Payable",
      query: `SELECT 
        ${FY_MONTH("vi.invoice_date")} AS fy_month_order,
        ${FY_MONTH_NAME("vi.invoice_date")} AS month,
        avg(dateDiff('day', vi.invoice_date, coalesce(p.payment_date, today()))) AS dpo
      FROM finance.vendor_invoices vi
      LEFT JOIN finance.payments p ON vi.invoice_id=p.reference_invoice
      WHERE 1=1 ${buildDateFilter(fy, "invoice_date", "vi")}
      ${entity !== "ALL" ? `AND vi.entity_id = ${entity}` : ""}
      GROUP BY fy_month_order, month ORDER BY fy_month_order`,
      chartType: "line", xKey: "month", yKey: "dpo", fmt: "days",
    },
  ],
  COO: (fy, entity) => [
    {
      key: "orders_monthly",
      label: "Sales Orders Volume (Monthly)",
      category: "Order Management",
      query: `SELECT 
        ${FY_MONTH("order_date")} AS fy_month_order,
        ${FY_MONTH_NAME("order_date")} AS month,
        count() AS orders,
        sum(total_amount) AS value
      FROM sales.sales_orders
      WHERE 1=1 ${buildDateFilter(fy, "order_date")}
      ${buildEntityFilter(entity)}
      GROUP BY fy_month_order, month ORDER BY fy_month_order`,
      chartType: "composed", xKey: "month", yKeys: ["orders","value"], fmt: "mixed",
    },
    {
      key: "fulfillment_rate",
      label: "Order Fulfilment Rate (Monthly)",
      category: "Order Management",
      query: `SELECT 
        ${FY_MONTH("so.order_date")} AS fy_month_order,
        ${FY_MONTH_NAME("so.order_date")} AS month,
        round(countIf(d.delivery_id IS NOT NULL)*100.0/count(),2) AS fulfillment_rate
      FROM sales.sales_orders so
      LEFT JOIN sales.deliveries d ON so.sales_order_id=d.sales_order_id
      WHERE 1=1 ${buildDateFilter(fy, "order_date", "so")}
      ${entity !== "ALL" ? `AND so.entity_id = ${entity}` : ""}
      GROUP BY fy_month_order, month ORDER BY fy_month_order`,
      chartType: "line", xKey: "month", yKey: "fulfillment_rate", fmt: "percent",
    },
    {
      key: "po_cycle",
      label: "PO Cycle Time (Monthly Avg Days)",
      category: "Procurement",
      query: `SELECT 
        ${FY_MONTH("po.po_date")} AS fy_month_order,
        ${FY_MONTH_NAME("po.po_date")} AS month,
        avg(dateDiff('day', po.po_date, gr.receipt_date)) AS cycle_days
      FROM procurement.purchase_orders po
      JOIN procurement.goods_receipts gr ON po.po_id=gr.po_id
      WHERE 1=1 ${buildDateFilter(fy, "po_date", "po")}
      ${entity !== "ALL" ? `AND po.entity_id = ${entity}` : ""}
      GROUP BY fy_month_order, month ORDER BY fy_month_order`,
      chartType: "line", xKey: "month", yKey: "cycle_days", fmt: "days",
    },
    {
      key: "vendor_spend",
      label: "Spend by Vendor Category",
      category: "Procurement",
      query: `SELECT v.vendor_category, sum(po.total_amount) AS spend
              FROM procurement.purchase_orders po
              JOIN erp_core.vendors v ON po.vendor_id=v.vendor_id
              WHERE 1=1 ${buildDateFilter(fy, "po_date", "po")}
              ${entity !== "ALL" ? `AND po.entity_id = ${entity}` : ""}
              GROUP BY v.vendor_category ORDER BY spend DESC`,
      chartType: "pie", xKey: "vendor_category", yKey: "spend", fmt: "currency",
    },
    {
      key: "delivery_perf",
      label: "Delivery Performance Trend",
      category: "Order Management",
      query: `SELECT 
        ${FY_MONTH("so.order_date")} AS fy_month_order,
        ${FY_MONTH_NAME("so.order_date")} AS month,
        avg(dateDiff('day', so.order_date, d.delivery_date)) AS avg_delivery_days
      FROM sales.sales_orders so
      JOIN sales.deliveries d ON so.sales_order_id=d.sales_order_id
      WHERE 1=1 ${buildDateFilter(fy, "order_date", "so")}
      ${entity !== "ALL" ? `AND so.entity_id = ${entity}` : ""}
      GROUP BY fy_month_order, month ORDER BY fy_month_order`,
      chartType: "line", xKey: "month", yKey: "avg_delivery_days", fmt: "days",
    },
    {
      key: "po_status",
      label: "PO Status Distribution",
      category: "Procurement",
      query: `SELECT status, count() AS count, sum(total_amount) AS value
              FROM procurement.purchase_orders
              WHERE 1=1 ${buildDateFilter(fy, "po_date")}
              ${buildEntityFilter(entity)}
              GROUP BY status`,
      chartType: "pie", xKey: "status", yKey: "count", fmt: "number",
    },
  ],
  VPS: (fy, entity) => [
    {
      key: "revenue_by_customer",
      label: "Top 10 Customers by Revenue",
      category: "Pipeline & Revenue",
      query: `SELECT c.customer_name, c.industry, sum(so.total_amount) AS revenue
              FROM sales.sales_orders so
              JOIN erp_core.customers c ON so.customer_id=c.customer_id
              WHERE so.order_status NOT IN ('CANCELLED')
              ${buildDateFilter(fy, "order_date", "so")}
              ${entity !== "ALL" ? `AND so.entity_id = ${entity}` : ""}
              GROUP BY c.customer_name, c.industry ORDER BY revenue DESC LIMIT 10`,
      chartType: "bar", xKey: "customer_name", yKey: "revenue", fmt: "currency",
    },
    {
      key: "revenue_by_material",
      label: "Revenue by Product / Material",
      category: "Pipeline & Revenue",
      query: `SELECT m.category, sum(sol.line_amount) AS revenue, sum(sol.quantity) AS qty
              FROM sales.sales_order_lines sol
              JOIN erp_core.materials m ON sol.material_id=m.material_id
              JOIN sales.sales_orders so ON sol.sales_order_id=so.sales_order_id
              WHERE so.order_status NOT IN ('CANCELLED')
              ${buildDateFilter(fy, "order_date", "so")}
              ${entity !== "ALL" ? `AND sol.entity_id = ${entity}` : ""}
              GROUP BY m.category ORDER BY revenue DESC`,
      chartType: "pie", xKey: "category", yKey: "revenue", fmt: "currency",
    },
    {
      key: "bookings_trend",
      label: "Monthly Bookings Trend",
      category: "Pipeline & Revenue",
      query: `SELECT 
        ${FY_MONTH("order_date")} AS fy_month_order,
        ${FY_MONTH_NAME("order_date")} AS month,
        sum(total_amount) AS bookings,
        count() AS orders
      FROM sales.sales_orders
      WHERE order_status NOT IN ('CANCELLED')
      ${buildDateFilter(fy, "order_date")}
      ${buildEntityFilter(entity)}
      GROUP BY fy_month_order, month ORDER BY fy_month_order`,
      chartType: "area", xKey: "month", yKey: "bookings", fmt: "currency",
    },
    {
      key: "aov_trend",
      label: "Average Order Value Trend",
      category: "Pipeline & Revenue",
      query: `SELECT 
        ${FY_MONTH("order_date")} AS fy_month_order,
        ${FY_MONTH_NAME("order_date")} AS month,
        round(sum(total_amount)/count(),2) AS aov
      FROM sales.sales_orders
      WHERE order_status NOT IN ('CANCELLED')
      ${buildDateFilter(fy, "order_date")}
      ${buildEntityFilter(entity)}
      GROUP BY fy_month_order, month ORDER BY fy_month_order`,
      chartType: "line", xKey: "month", yKey: "aov", fmt: "currency",
    },
    {
      key: "new_customers",
      label: "New Customer Acquisition",
      category: "Customer Metrics",
      query: `SELECT 
        ${FY_MONTH("first_order")} AS fy_month_order,
        ${FY_MONTH_NAME("first_order")} AS month,
        count() AS new_customers
      FROM (
        SELECT customer_id, min(order_date) AS first_order
        FROM sales.sales_orders
        WHERE order_status NOT IN ('CANCELLED')
        ${buildDateFilter(fy, "order_date")}
        ${buildEntityFilter(entity)}
        GROUP BY customer_id
      )
      GROUP BY fy_month_order, month ORDER BY fy_month_order`,
      chartType: "bar", xKey: "month", yKey: "new_customers", fmt: "number",
    },
    {
      key: "asp",
      label: "Average Selling Price by Material",
      category: "Pipeline & Revenue",
      query: `SELECT m.material_name, round(sum(sol.line_amount)/nullIf(sum(sol.quantity),0),2) AS asp, sum(sol.quantity) AS qty
              FROM sales.sales_order_lines sol
              JOIN erp_core.materials m ON sol.material_id=m.material_id
              JOIN sales.sales_orders so ON sol.sales_order_id=so.sales_order_id
              WHERE so.order_status NOT IN ('CANCELLED')
              ${buildDateFilter(fy, "order_date", "so")}
              ${entity !== "ALL" ? `AND sol.entity_id = ${entity}` : ""}
              GROUP BY m.material_name ORDER BY qty DESC LIMIT 15`,
      chartType: "bar", xKey: "material_name", yKey: "asp", fmt: "currency",
    },
  ],
  VPP: (fy, entity) => [
    {
      key: "spend_trend",
      label: "Procurement Spend Trend",
      category: "Spend Analytics",
      query: `SELECT 
        ${FY_MONTH("po_date")} AS fy_month_order,
        ${FY_MONTH_NAME("po_date")} AS month,
        sum(total_amount) AS spend,
        count() AS po_count
      FROM procurement.purchase_orders
      WHERE 1=1 ${buildDateFilter(fy, "po_date")}
      ${buildEntityFilter(entity)}
      GROUP BY fy_month_order, month ORDER BY fy_month_order`,
      chartType: "area", xKey: "month", yKey: "spend", fmt: "currency",
    },
    {
      key: "top_vendors",
      label: "Top Vendors by Spend",
      category: "Spend Analytics",
      query: `SELECT v.vendor_name, v.vendor_category, sum(po.total_amount) AS spend
              FROM procurement.purchase_orders po
              JOIN erp_core.vendors v ON po.vendor_id=v.vendor_id
              WHERE 1=1 ${buildDateFilter(fy, "po_date", "po")}
              ${entity !== "ALL" ? `AND po.entity_id = ${entity}` : ""}
              GROUP BY v.vendor_name, v.vendor_category ORDER BY spend DESC LIMIT 10`,
      chartType: "bar", xKey: "vendor_name", yKey: "spend", fmt: "currency",
    },
    {
      key: "ppv",
      label: "Purchase Price Variance by Material",
      category: "Spend Analytics",
      query: `SELECT m.material_name, sum((pol.unit_price - m.standard_cost)*pol.quantity) AS ppv
              FROM procurement.po_lines pol
              JOIN erp_core.materials m ON pol.material_id=m.material_id
              JOIN procurement.purchase_orders po ON pol.po_id=po.po_id
              WHERE 1=1 ${buildDateFilter(fy, "po_date", "po")}
              ${entity !== "ALL" ? `AND pol.entity_id = ${entity}` : ""}
              GROUP BY m.material_name ORDER BY abs(ppv) DESC LIMIT 15`,
      chartType: "bar", xKey: "material_name", yKey: "ppv", fmt: "currency",
    },
    {
      key: "vendor_perf",
      label: "Vendor On-Time Delivery Rate (Monthly)",
      category: "Vendor Performance",
      query: `SELECT 
        ${FY_MONTH("po.po_date")} AS fy_month_order,
        ${FY_MONTH_NAME("po.po_date")} AS month,
        round(countIf(gr.receipt_date <= pol.delivery_date)*100.0/count(),2) AS on_time_rate
      FROM procurement.purchase_orders po
      JOIN procurement.goods_receipts gr ON po.po_id=gr.po_id
      JOIN procurement.po_lines pol ON po.po_id=pol.po_id
      WHERE 1=1 ${buildDateFilter(fy, "po_date", "po")}
      ${entity !== "ALL" ? `AND po.entity_id = ${entity}` : ""}
      GROUP BY fy_month_order, month ORDER BY fy_month_order`,
      chartType: "line", xKey: "month", yKey: "on_time_rate", fmt: "percent",
    },
    {
      key: "vendor_category_spend",
      label: "Spend Distribution by Category",
      category: "Spend Analytics",
      query: `SELECT v.vendor_category, sum(po.total_amount) AS spend
              FROM procurement.purchase_orders po
              JOIN erp_core.vendors v ON po.vendor_id=v.vendor_id
              WHERE 1=1 ${buildDateFilter(fy, "po_date", "po")}
              ${entity !== "ALL" ? `AND po.entity_id = ${entity}` : ""}
              GROUP BY v.vendor_category ORDER BY spend DESC`,
      chartType: "pie", xKey: "vendor_category", yKey: "spend", fmt: "currency",
    },
    {
      key: "active_vendors",
      label: "Active Vendors by Year",
      category: "Vendor Performance",
      query: `SELECT toYear(po_date) AS yr, count(DISTINCT vendor_id) AS active_vendors
              FROM procurement.purchase_orders
              WHERE 1=1 ${buildEntityFilter(entity)}
              GROUP BY yr ORDER BY yr`,
      chartType: "bar", xKey: "yr", yKey: "active_vendors", fmt: "number",
    },
  ],
  VPH: (fy, entity) => [
    {
      key: "headcount",
      label: "Headcount Trend",
      category: "Workforce",
      query: `SELECT 
        ${FY_MONTH("hire_date")} AS fy_month_order,
        ${FY_MONTH_NAME("hire_date")} AS month,
        count() AS headcount
      FROM erp_core.employees
      WHERE status='ACTIVE'
      ${buildDateFilter(fy, "hire_date")}
      ${buildEntityFilter(entity)}
      GROUP BY fy_month_order, month ORDER BY fy_month_order`,
      chartType: "area", xKey: "month", yKey: "headcount", fmt: "number",
    },
    {
      key: "attrition",
      label: "Attrition Trend",
      category: "Workforce",
      query: `SELECT 
        ${FY_MONTH("exit_date")} AS fy_month_order,
        ${FY_MONTH_NAME("exit_date")} AS month,
        count() AS exits
      FROM erp_core.employees
      WHERE status='INACTIVE'
      ${buildDateFilter(fy, "exit_date")}
      ${buildEntityFilter(entity)}
      GROUP BY fy_month_order, month ORDER BY fy_month_order`,
      chartType: "line", xKey: "month", yKey: "exits", fmt: "number",
    },
    {
      key: "designation_mix",
      label: "Designation Mix",
      category: "Workforce",
      query: `SELECT designation, count() AS count
              FROM erp_core.employees
              WHERE status='ACTIVE'
              ${buildEntityFilter(entity)}
              GROUP BY designation ORDER BY count DESC`,
      chartType: "pie", xKey: "designation", yKey: "count", fmt: "number",
    },
    {
      key: "avg_tenure",
      label: "Average Tenure",
      category: "Workforce",
      query: `SELECT avg(dateDiff('day', hire_date, today())/365) AS avg_tenure
              FROM erp_core.employees
              WHERE status='ACTIVE'
              ${buildEntityFilter(entity)}`,
      chartType: "kpi",
    },
  ],
  FM: (fy, entity) => [
    {
      key: "payment_run",
      label: "Payment Run Trend",
      category: "Finance Ops",
      query: `SELECT 
        ${FY_MONTH("payment_date")} AS fy_month_order,
        ${FY_MONTH_NAME("payment_date")} AS month,
        count() AS payment_count,
        sum(amount) AS payment_value
      FROM finance.payments
      WHERE 1=1 ${buildDateFilter(fy, "payment_date")}
      ${buildEntityFilter(entity)}
      GROUP BY fy_month_order, month ORDER BY fy_month_order`,
      chartType: "composed", xKey: "month", yKeys: ["payment_count","payment_value"], fmt: "mixed",
    },
    {
      key: "outstanding_invoices",
      label: "Outstanding Invoices Trend",
      category: "Finance Ops",
      query: `SELECT 
        ${FY_MONTH("invoice_date")} AS fy_month_order,
        ${FY_MONTH_NAME("invoice_date")} AS month,
        count() AS open_invoices,
        sum(invoice_amount) AS outstanding_value
      FROM finance.vendor_invoices vi
      LEFT JOIN finance.payments p ON vi.invoice_id=p.reference_invoice
      WHERE p.payment_id IS NULL
      ${buildDateFilter(fy, "invoice_date", "vi")}
      ${entity !== "ALL" ? `AND vi.entity_id = ${entity}` : ""}
      GROUP BY fy_month_order, month ORDER BY fy_month_order`,
      chartType: "composed", xKey: "month", yKeys: ["open_invoices","outstanding_value"], fmt: "mixed",
    },
    {
      key: "je_volume",
      label: "Journal Entry Volume",
      category: "Finance Ops",
      query: `SELECT toYear(posting_date) AS yr, count() AS entries
              FROM finance.universal_journal
              WHERE 1=1 ${buildEntityFilter(entity)}
              GROUP BY yr ORDER BY yr`,
      chartType: "bar", xKey: "yr", yKey: "entries", fmt: "number",
    },
    {
      key: "top_gl_accounts",
      label: "Top GL Accounts",
      category: "Finance Ops",
      query: `SELECT gl_account, sum(amount_local) AS balance
              FROM finance.universal_journal
              WHERE 1=1 ${buildDateFilter(fy, "posting_date")}
              ${buildEntityFilter(entity)}
              GROUP BY gl_account ORDER BY abs(balance) DESC LIMIT 10`,
      chartType: "bar", xKey: "gl_account", yKey: "balance", fmt: "currency",
    },
    {
      key: "dr_cr_balance",
      label: "Debit vs Credit",
      category: "Finance Ops",
      query: `SELECT debit_credit_indicator, sum(amount_local) AS total
              FROM finance.universal_journal
              WHERE 1=1 ${buildDateFilter(fy, "posting_date")}
              ${buildEntityFilter(entity)}
              GROUP BY debit_credit_indicator`,
      chartType: "pie", xKey: "debit_credit_indicator", yKey: "total", fmt: "currency",
    },
  ],
  CCM: (fy, entity) => [
    {
      key: "collection_efficiency",
      label: "Collection Efficiency",
      category: "Collections",
      query: `SELECT 
        ${FY_MONTH("receipt_date")} AS fy_month_order,
        ${FY_MONTH_NAME("receipt_date")} AS month,
        sum(amount) AS collections
      FROM finance.receipts
      WHERE 1=1 ${buildDateFilter(fy, "receipt_date")}
      ${buildEntityFilter(entity)}
      GROUP BY fy_month_order, month ORDER BY fy_month_order`,
      chartType: "area", xKey: "month", yKey: "collections", fmt: "currency",
    },
    {
      key: "overdue_invoices",
      label: "Overdue Invoices",
      category: "Collections",
      query: `SELECT count() AS overdue_count, sum(invoice_amount) AS overdue_amount
              FROM finance.customer_invoices
              WHERE dateDiff('day', invoice_date, today()) > 30
              ${buildEntityFilter(entity)}`,
      chartType: "kpi",
    },
  ],
  OPS: (fy, entity) => [
    {
      key: "backlog",
      label: "Backlog Order Value",
      category: "Operations",
      query: `SELECT 
        ${FY_MONTH("order_date")} AS fy_month_order,
        ${FY_MONTH_NAME("order_date")} AS month,
        sum(total_amount) AS backlog_value,
        count() AS backlog_count
      FROM sales.sales_orders
      WHERE order_status='OPEN'
      ${buildDateFilter(fy, "order_date")}
      ${buildEntityFilter(entity)}
      GROUP BY fy_month_order, month ORDER BY fy_month_order`,
      chartType: "composed", xKey: "month", yKeys: ["backlog_value","backlog_count"], fmt: "mixed",
    },
    {
      key: "delivery_trend",
      label: "Delivery Trend",
      category: "Operations",
      query: `SELECT 
        ${FY_MONTH("delivery_date")} AS fy_month_order,
        ${FY_MONTH_NAME("delivery_date")} AS month,
        count() AS deliveries
      FROM sales.deliveries
      WHERE 1=1 ${buildDateFilter(fy, "delivery_date")}
      ${buildEntityFilter(entity)}
      GROUP BY fy_month_order, month ORDER BY fy_month_order`,
      chartType: "area", xKey: "month", yKey: "deliveries", fmt: "number",
    },
  ],
  DH: (fy, entity) => [
    {
      key: "department_cost",
      label: "Department Cost",
      category: "Department",
      query: `SELECT department_id, sum(amount_local) AS cost
              FROM finance.universal_journal
              WHERE account_type='Expense'
              ${buildDateFilter(fy, "posting_date")}
              ${buildEntityFilter(entity)}
              GROUP BY department_id ORDER BY cost DESC`,
      chartType: "bar", xKey: "department_id", yKey: "cost", fmt: "currency",
    },
    {
      key: "department_headcount",
      label: "Department Headcount",
      category: "Department",
      query: `SELECT department_id, count() AS headcount
              FROM erp_core.employees
              WHERE status='ACTIVE'
              ${buildEntityFilter(entity)}
              GROUP BY department_id ORDER BY headcount DESC`,
      chartType: "bar", xKey: "department_id", yKey: "headcount", fmt: "number",
    },
  ],
  DPH: (fy, entity) => [
    {
      key: "project_cost",
      label: "Project Cost",
      category: "Projects",
      query: `SELECT profit_center, sum(amount_local) AS cost
              FROM finance.universal_journal
              WHERE account_type='Expense'
              ${buildDateFilter(fy, "posting_date")}
              ${buildEntityFilter(entity)}
              GROUP BY profit_center ORDER BY cost DESC`,
      chartType: "bar", xKey: "profit_center", yKey: "cost", fmt: "currency",
    },
    {
      key: "project_revenue",
      label: "Project Revenue",
      category: "Projects",
      query: `SELECT profit_center, sum(total_amount) AS revenue
              FROM sales.sales_orders
              WHERE order_status NOT IN ('CANCELLED')
              ${buildDateFilter(fy, "order_date")}
              ${buildEntityFilter(entity)}
              GROUP BY profit_center ORDER BY revenue DESC`,
      chartType: "bar", xKey: "profit_center", yKey: "revenue", fmt: "currency",
    },
  ],
};

/* ═══════════════════════════════════════════════
   FORMATTERS
═══════════════════════════════════════════════ */
const fmt = {
  currency: (v) => {
    if (v == null || isNaN(v)) return "—";
    if (Math.abs(v) >= 1e9) return `₹${(v/1e9).toFixed(2)}B`;
    if (Math.abs(v) >= 1e6) return `₹${(v/1e6).toFixed(2)}M`;
    if (Math.abs(v) >= 1e3) return `₹${(v/1e3).toFixed(1)}K`;
    return `₹${Number(v).toFixed(0)}`;
  },
  number:  (v) => v == null ? "—" : Number(v).toLocaleString("en-IN"),
  percent: (v) => v == null ? "—" : `${Number(v).toFixed(1)}%`,
  days:    (v) => v == null ? "—" : `${Number(v).toFixed(1)}d`,
  mixed:   (v) => fmt.number(v),
};

const fmtVal = (v, f) => (fmt[f] || fmt.number)(v);

/* ═══════════════════════════════════════════════
   MONTH FORMATTER — fixes "Invalid Date"
   ClickHouse returns full date string e.g. '2024-04-01'
   We force local-time parse by appending T00:00:00
═══════════════════════════════════════════════ */
const parseMonthLabel = (raw) => {
  if (!raw) return raw;
  // Already a short name like "Apr" — return as-is
  if (/^[A-Za-z]{3}/.test(raw)) return raw;
  // Full date string "2024-04-01" or "2024-04"
  const normalized = raw.length === 7 ? raw + "-01" : raw;
  const d = new Date(normalized + "T00:00:00");
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleString("en-IN", { month: "short", year: "2-digit" });
};

/* ═══════════════════════════════════════════════
   SUMMARY KPI BAR
═══════════════════════════════════════════════ */
const SUMMARY_KPIS = {
  CEO: (data) => [
    { label:"Total Group Revenue", value: fmt.currency(data.revenue_trend?.reduce((s,r)=>s+(r.revenue||0),0)),     color: C.blue  },
    { label:"Active Entities",     value: fmt.number(data.revenue_by_entity?.length || 0),                         color: C.navy  },
    { label:"Total Headcount",     value: fmt.number(data.headcount?.reduce((s,r)=>s+(r.headcount||0),0)),          color: C.teal  },
    { label:"Cashflow (YTD)",      value: fmt.currency(data.cashflow?.reduce((s,r)=>s+(r.inflow||0),0)),           color: C.green },
  ],
  CFO: (data) => [
    { label:"DSO (Latest Month)",  value: fmt.days(data.dso?.slice(-1)[0]?.dso),                                   color: C.red   },
    { label:"DPO (Latest Month)",  value: fmt.days(data.dpo?.slice(-1)[0]?.dpo),                                   color: C.amber },
    { label:"AR Overdue > 90d",    value: fmt.currency(data.ar_aging?.find(r=>r.bucket?.includes("90"))?.amount),  color: C.red   },
    { label:"Outstanding AP",      value: fmt.currency(data.ap_aging?.reduce((s,r)=>s+(r.amount||0),0)),           color: C.amber },
  ],
  COO: (data) => [
    { label:"Orders (YTD)",        value: fmt.number(data.orders_monthly?.reduce((s,r)=>s+(r.orders||0),0)),       color: C.blue  },
    { label:"Avg Fulfillment Rate",value: fmt.percent(data.fulfillment_rate?.slice(-3).reduce((s,r)=>s+(r.fulfillment_rate||0),0)/3), color: C.teal },
    { label:"Avg PO Cycle Days",   value: fmt.days(data.po_cycle?.slice(-1)[0]?.cycle_days),                       color: C.amber },
    { label:"Total Proc Spend",    value: fmt.currency(data.vendor_spend?.reduce((s,r)=>s+(r.spend||0),0)),        color: C.navy  },
  ],
  VPS: (data) => [
    { label:"Total Bookings (YTD)",value: fmt.currency(data.bookings_trend?.reduce((s,r)=>s+(r.bookings||0),0)),   color: C.blue  },
    { label:"New Customers",       value: fmt.number(data.new_customers?.reduce((s,r)=>s+(r.new_customers||0),0)), color: C.green },
    { label:"Avg Order Value",     value: fmt.currency(data.aov_trend?.slice(-1)[0]?.aov),                        color: C.teal  },
    { label:"Top 10 Cust Revenue", value: fmt.currency(data.revenue_by_customer?.reduce((s,r)=>s+(r.revenue||0),0)), color: C.purple },
  ],
  VPP: (data) => [
    { label:"Total Proc Spend",    value: fmt.currency(data.spend_trend?.reduce((s,r)=>s+(r.spend||0),0)),         color: C.blue  },
    { label:"Active Vendors",      value: fmt.number(data.active_vendors?.slice(-1)[0]?.active_vendors),           color: C.teal  },
    { label:"On-Time Delivery",    value: fmt.percent(data.vendor_perf?.slice(-1)[0]?.on_time_rate),               color: C.green },
    { label:"Top Vendor Spend",    value: fmt.currency(data.top_vendors?.[0]?.spend),                              color: C.amber },
  ],
  VPH: (data) => [
    { label:"Total Headcount",     value: fmt.number(data.headcount?.reduce((s,r)=>s+(r.headcount||0),0)),         color: C.blue  },
    { label:"New Hires (YTD)",     value: fmt.number(data.new_hires?.reduce((s,r)=>s+(r.new_hires||0),0)),         color: C.green },
    { label:"Avg Attrition",       value: fmt.percent(data.attrition?.reduce((s,r)=>s+(r.exits||0),0)),            color: C.red   },
    { label:"Designation Groups",  value: fmt.number(data.designation_mix?.length || 0),                          color: C.amber },
  ],
  FM: (data) => [
    { label:"JE Lines (YTD)",      value: fmt.number(data.je_volume?.reduce((s,r)=>s+(r.entries||0),0)),           color: C.blue  },
    { label:"Total Payments",      value: fmt.currency(data.payment_run?.reduce((s,r)=>s+(r.payment_value||0),0)), color: C.green },
    { label:"Outstanding Invoices",value: fmt.currency(data.outstanding_invoices?.reduce((s,r)=>s+(r.outstanding_value||0),0)), color: C.red },
    { label:"Top GL Accounts",     value: fmt.number(data.top_gl_accounts?.length || 0),                           color: C.teal  },
  ],
  CCM: (data) => [
    { label:"Collections (YTD)",   value: fmt.currency(data.collection_efficiency?.reduce((s,r)=>s+(r.collections||0),0)), color: C.blue },
    { label:"Overdue Count",       value: fmt.number(data.overdue_invoices?.[0]?.overdue_count),                   color: C.red   },
    { label:"Overdue Amount",      value: fmt.currency(data.overdue_invoices?.[0]?.overdue_amount),                color: C.amber },
    { label:"—", value: "—", color: C.teal },
  ],
  OPS: (data) => [
    { label:"Backlog Value",       value: fmt.currency(data.backlog?.reduce((s,r)=>s+(r.backlog_value||0),0)),     color: C.red   },
    { label:"Total Deliveries",    value: fmt.number(data.delivery_trend?.reduce((s,r)=>s+(r.deliveries||0),0)),   color: C.green },
    { label:"—", value: "—", color: C.blue },
    { label:"—", value: "—", color: C.amber },
  ],
  DH: (data) => [
    { label:"Dept Cost",           value: fmt.currency(data.department_cost?.reduce((s,r)=>s+(r.cost||0),0)),      color: C.blue  },
    { label:"Total Headcount",     value: fmt.number(data.department_headcount?.reduce((s,r)=>s+(r.headcount||0),0)), color: C.teal },
    { label:"—", value: "—", color: C.amber },
    { label:"—", value: "—", color: C.green },
  ],
  DPH: (data) => [
    { label:"Project Cost",        value: fmt.currency(data.project_cost?.reduce((s,r)=>s+(r.cost||0),0)),         color: C.blue  },
    { label:"Project Revenue",     value: fmt.currency(data.project_revenue?.reduce((s,r)=>s+(r.revenue||0),0)),   color: C.green },
    { label:"—", value: "—", color: C.teal },
    { label:"—", value: "—", color: C.amber },
  ],
};

/* ═══════════════════════════════════════════════
   COMPONENTS
═══════════════════════════════════════════════ */
const Shimmer = ({ h = 20, w = "100%", r = 6 }) => (
  <div style={{ width: w, height: h, borderRadius: r, background: "linear-gradient(90deg,#E8ECF4 25%,#F4F6FC 50%,#E8ECF4 75%)", backgroundSize:"200% 100%", animation:"shimmer 1.4s infinite" }} />
);

const KpiTile = ({ label, value, color = C.navyMid, loading }) => (
  <div style={{
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: "10px 16px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: 5,
    borderLeft: `4px solid ${color}`,
    width: "100%",
    minWidth: 0,
    flex: 1,
    height: "100%",
  }}>
    <span style={{
      fontSize: 11,
      fontWeight: 600,
      color: C.textSub,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      fontFamily: C.sans,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }}>{label}</span>
    {loading
      ? <Shimmer h={28} w="70%" />
      : <span style={{
          fontSize: 26,
          fontWeight: 700,
          color: C.text,
          fontFamily: C.mono,
          lineHeight: 1,
          letterSpacing: "-0.02em",
        }}>{value}</span>
    }
  </div>
);

const ChartCard = ({ title, category, children, onDrilldown, onExport, query }) => {
  const [showQuery, setShowQuery] = useState(false);
  return (
    <div style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 10,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      height: "100%",
    }}>
      <div style={{
        padding: "7px 12px 6px",
        borderBottom: `1px solid ${C.border}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: C.surfaceAlt,
        flexShrink: 0,
      }}>
        <div>
          <div style={{
            fontSize: 10,
            color: C.textMut,
            fontWeight: 700,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            fontFamily: C.sans,
          }}>{category}</div>
          <div style={{
            fontSize: 12,
            fontWeight: 700,
            color: C.text,
            fontFamily: C.head,
            marginTop: 1,
            lineHeight: 1.2,
          }}>{title}</div>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {query && (
            <button onClick={() => setShowQuery(s => !s)} style={{
              background: "none", border: `1px solid ${C.border}`, borderRadius: 4,
              padding: "2px 7px", cursor: "pointer", fontSize: 10, color: C.textSub, fontFamily: C.sans,
            }}>SQL</button>
          )}
          {onDrilldown && (
            <button onClick={onDrilldown} style={{
              background: "none", border: `1px solid ${C.border}`, borderRadius: 4,
              padding: "2px 7px", cursor: "pointer", fontSize: 10, color: C.textSub, fontFamily: C.sans,
            }}>⊕</button>
          )}
          <button onClick={onExport} style={{
            background: "none", border: `1px solid ${C.border}`, borderRadius: 4,
            padding: "2px 7px", cursor: "pointer", fontSize: 10, color: C.textSub, fontFamily: C.sans,
          }}>↓CSV</button>
        </div>
      </div>
      {showQuery && (
        <div style={{
          background: "#1E293B", padding: "8px 12px", fontFamily: C.mono, fontSize: 10,
          color: "#94A3B8", lineHeight: 1.5, overflowX: "auto", whiteSpace: "pre-wrap",
          wordBreak: "break-word", maxHeight: 120, overflowY: "auto", flexShrink: 0,
        }}>
          <span style={{ color: "#64B5F6" }}>-- ClickHouse Query{"\n"}</span>
          {query}
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0, padding: "8px 12px", display: "flex", flexDirection: "column" }}>
        {children}
      </div>
    </div>
  );
};

const CustomTT = ({ active, payload, label, fmtType = "currency" }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:C.surface, border:`1px solid ${C.borderMed}`, borderRadius:8, padding:"7px 10px", fontSize:11, fontFamily:C.sans, boxShadow:"0 6px 20px rgba(0,0,0,0.12)" }}>
      <p style={{ color:C.textSub, marginBottom:4, fontSize:10 }}>{label}</p>
      {payload.map((p,i) => (
        <p key={i} style={{ color:p.color||C.text, fontFamily:C.mono, fontWeight:600, marginBottom:1, fontSize:11 }}>
          <span style={{ color:C.textSub, fontWeight:400, fontFamily:C.sans }}>{p.name}: </span>
          {fmtVal(p.value, fmtType)}
        </p>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════════
   GROUPED DRILL QUERIES (≤60 rows each)
═══════════════════════════════════════════════ */
const DRILL_QUERIES = {
  revenue_trend:      (fy,ent) => `SELECT e.entity_name, formatDateTime(so.order_date,'%b') AS month, count() AS orders, sum(so.total_amount) AS revenue, round(avg(so.total_amount),0) AS avg_order FROM sales.sales_orders so JOIN erp_core.entities e ON so.entity_id=e.entity_id WHERE so.order_status NOT IN ('CANCELLED') ${buildDateFilter(fy,"order_date","so")} ${buildEntityFilter(ent,"so")} GROUP BY e.entity_name,month ORDER BY revenue DESC LIMIT 60`,
  revenue_by_entity:  (fy,ent) => `SELECT e.entity_name, c.industry, count(DISTINCT so.customer_id) AS customers, count() AS orders, sum(so.total_amount) AS revenue FROM sales.sales_orders so JOIN erp_core.entities e ON so.entity_id=e.entity_id JOIN erp_core.customers c ON so.customer_id=c.customer_id WHERE so.order_status NOT IN ('CANCELLED') ${buildDateFilter(fy,"order_date","so")} ${buildEntityFilter(ent,"so")} GROUP BY e.entity_name,c.industry ORDER BY revenue DESC LIMIT 50`,
  yoy_growth:         (fy,ent) => `SELECT toYear(order_date) AS yr, e.entity_name, count() AS orders, sum(total_amount) AS revenue FROM sales.sales_orders so JOIN erp_core.entities e ON so.entity_id=e.entity_id WHERE order_status NOT IN ('CANCELLED') ${buildEntityFilter(ent,"so")} GROUP BY yr,e.entity_name ORDER BY yr DESC,revenue DESC LIMIT 60`,
  balance_sheet:      (fy,ent) => `SELECT account_type, gl_account, sum(CASE WHEN debit_credit_indicator='D' THEN amount_local ELSE -amount_local END) AS balance, count() AS entries FROM finance.universal_journal WHERE account_type IN ('Asset','Liability','Equity') ${buildDateFilter(fy,"posting_date")} ${buildEntityFilter(ent)} GROUP BY account_type,gl_account ORDER BY account_type,abs(balance) DESC LIMIT 60`,
  cashflow:           (fy,ent) => `SELECT formatDateTime(receipt_date,'%b') AS month, count(DISTINCT customer_id) AS customers, count() AS receipts, sum(amount) AS inflow, round(avg(amount),0) AS avg_receipt FROM finance.receipts WHERE 1=1 ${buildDateFilter(fy,"receipt_date")} ${buildEntityFilter(ent)} GROUP BY month ORDER BY inflow DESC LIMIT 30`,
  headcount:          (fy,ent) => `SELECT e.entity_name, emp.designation, emp.department_id, count() AS headcount FROM erp_core.employees emp JOIN erp_core.entities e ON emp.entity_id=e.entity_id WHERE emp.status='ACTIVE' ${buildEntityFilter(ent,"emp")} GROUP BY e.entity_name,emp.designation,emp.department_id ORDER BY headcount DESC LIMIT 60`,
  ar_aging:           (fy,ent) => `SELECT c.customer_name, c.industry, count() AS invoices, sum(ci.invoice_amount) AS outstanding, round(avg(dateDiff('day',ci.invoice_date,today())),1) AS avg_days_open FROM finance.customer_invoices ci LEFT JOIN finance.receipts r ON ci.invoice_id=r.invoice_id JOIN erp_core.customers c ON ci.customer_id=c.customer_id WHERE r.receipt_id IS NULL ${buildEntityFilter(ent,"ci")} GROUP BY c.customer_name,c.industry ORDER BY outstanding DESC LIMIT 50`,
  ap_aging:           (fy,ent) => `SELECT v.vendor_name, v.vendor_category, count() AS invoices, sum(vi.invoice_amount) AS outstanding, round(avg(dateDiff('day',vi.invoice_date,today())),1) AS avg_days_open FROM finance.vendor_invoices vi LEFT JOIN finance.payments p ON vi.invoice_id=p.reference_invoice JOIN erp_core.vendors v ON vi.vendor_id=v.vendor_id WHERE p.payment_id IS NULL ${buildEntityFilter(ent,"vi")} GROUP BY v.vendor_name,v.vendor_category ORDER BY outstanding DESC LIMIT 50`,
  dso:                (fy,ent) => `SELECT 
    c.industry, 
    formatDateTime(ci.invoice_date, '%b') AS month, 
    count() AS invoices, 
    round(
        avg(dateDiff('day', ci.invoice_date, coalesce(r.receipt_date, today()))), 
        1
    ) AS avg_dso 

FROM finance.customer_invoices ci

LEFT JOIN finance.receipts r 
    ON ci.invoice_id = r.invoice_id

LEFT JOIN sales.sales_orders so 
    ON ci.sales_order_id = so.sales_order_id   -- ✅ FIX

LEFT JOIN erp_core.customers c 
    ON so.customer_id = c.customer_id         -- ✅ FIX

WHERE 1=1 
${buildDateFilter(fy,"invoice_date","ci")} 
${buildEntityFilter(ent,"ci")}

GROUP BY 
    c.industry, 
    month 

ORDER BY avg_dso DESC 
LIMIT 50;`,
  dpo:                (fy,ent) => `SELECT v.vendor_category, formatDateTime(vi.invoice_date,'%b') AS month, count() AS invoices, round(avg(dateDiff('day',vi.invoice_date,coalesce(p.payment_date,today()))),1) AS avg_dpo FROM finance.vendor_invoices vi LEFT JOIN finance.payments p ON vi.invoice_id=p.reference_invoice JOIN erp_core.vendors v ON vi.vendor_id=v.vendor_id WHERE 1=1 ${buildDateFilter(fy,"invoice_date","vi")} ${buildEntityFilter(ent,"vi")} GROUP BY v.vendor_category,month ORDER BY avg_dpo DESC LIMIT 50`,
  cashflow_monthly:   (fy,ent) => `SELECT formatDateTime(txn_date,'%b') AS month, bank_account, sum(CASE WHEN amount>0 THEN amount ELSE 0 END) AS cash_in, abs(sum(CASE WHEN amount<0 THEN amount ELSE 0 END)) AS cash_out, count() AS transactions FROM finance.bank_statement WHERE 1=1 ${buildDateFilter(fy,"txn_date")} ${buildEntityFilter(ent)} GROUP BY month,bank_account ORDER BY cash_in DESC LIMIT 50`,
  cost_by_division:   (fy,ent) => `SELECT d.org_name AS division, count(DISTINCT uj.profit_center) AS profit_centers, sum(uj.amount_local) AS cost FROM finance.universal_journal uj JOIN erp_core.division d ON uj.entity_id=d.entity_id WHERE uj.account_type='Expense' ${buildDateFilter(fy,"posting_date","uj")} ${buildEntityFilter(ent,"uj")} GROUP BY division ORDER BY cost DESC LIMIT 40`,
  orders_monthly:     (fy,ent) => `SELECT e.entity_name, formatDateTime(so.order_date,'%b') AS month, so.order_status, count() AS orders, sum(so.total_amount) AS value FROM sales.sales_orders so JOIN erp_core.entities e ON so.entity_id=e.entity_id WHERE 1=1 ${buildDateFilter(fy,"order_date","so")} ${buildEntityFilter(ent,"so")} GROUP BY e.entity_name,month,so.order_status ORDER BY value DESC LIMIT 60`,
  fulfillment_rate:   (fy,ent) => `SELECT e.entity_name, formatDateTime(so.order_date,'%b') AS month, count() AS total_orders, countIf(d.delivery_id IS NOT NULL) AS delivered, round(countIf(d.delivery_id IS NOT NULL)*100.0/count(),1) AS fulfillment_pct FROM sales.sales_orders so LEFT JOIN sales.deliveries d ON so.sales_order_id=d.sales_order_id JOIN erp_core.entities e ON so.entity_id=e.entity_id WHERE 1=1 ${buildDateFilter(fy,"order_date","so")} ${buildEntityFilter(ent,"so")} GROUP BY e.entity_name,month ORDER BY fulfillment_pct ASC LIMIT 50`,
  po_cycle:           (fy,ent) => `SELECT v.vendor_category, formatDateTime(po.po_date,'%b') AS month, count() AS po_count, round(avg(dateDiff('day',po.po_date,gr.receipt_date)),1) AS avg_days, round(min(dateDiff('day',po.po_date,gr.receipt_date)),1) AS min_days, round(max(dateDiff('day',po.po_date,gr.receipt_date)),1) AS max_days FROM procurement.purchase_orders po JOIN procurement.goods_receipts gr ON po.po_id=gr.po_id JOIN erp_core.vendors v ON po.vendor_id=v.vendor_id WHERE 1=1 ${buildDateFilter(fy,"po_date","po")} ${buildEntityFilter(ent,"po")} GROUP BY v.vendor_category,month ORDER BY avg_days DESC LIMIT 50`,
  vendor_spend:       (fy,ent) => `SELECT v.vendor_name, v.vendor_category, count() AS po_count, sum(po.total_amount) AS spend, round(avg(po.total_amount),0) AS avg_po FROM procurement.purchase_orders po JOIN erp_core.vendors v ON po.vendor_id=v.vendor_id WHERE 1=1 ${buildDateFilter(fy,"po_date","po")} ${buildEntityFilter(ent,"po")} GROUP BY v.vendor_name,v.vendor_category ORDER BY spend DESC LIMIT 50`,
  delivery_perf:      (fy,ent) => `SELECT e.entity_name, formatDateTime(so.order_date,'%b') AS month, count() AS deliveries, round(avg(dateDiff('day',so.order_date,d.delivery_date)),1) AS avg_days FROM sales.sales_orders so JOIN sales.deliveries d ON so.sales_order_id=d.sales_order_id JOIN erp_core.entities e ON so.entity_id=e.entity_id WHERE 1=1 ${buildDateFilter(fy,"order_date","so")} ${buildEntityFilter(ent,"so")} GROUP BY e.entity_name,month ORDER BY avg_days DESC LIMIT 50`,
  po_status:          (fy,ent) => `SELECT po.status, v.vendor_category, count() AS po_count, sum(po.total_amount) AS value FROM procurement.purchase_orders po JOIN erp_core.vendors v ON po.vendor_id=v.vendor_id WHERE 1=1 ${buildDateFilter(fy,"po_date","po")} ${buildEntityFilter(ent,"po")} GROUP BY po.status,v.vendor_category ORDER BY value DESC LIMIT 40`,
  revenue_by_customer:(fy,ent) => `SELECT c.customer_name, c.industry, count() AS orders, sum(so.total_amount) AS revenue, round(avg(so.total_amount),0) AS avg_order FROM sales.sales_orders so JOIN erp_core.customers c ON so.customer_id=c.customer_id WHERE so.order_status NOT IN ('CANCELLED') ${buildDateFilter(fy,"order_date","so")} ${buildEntityFilter(ent,"so")} GROUP BY c.customer_name,c.industry ORDER BY revenue DESC LIMIT 50`,
  revenue_by_material:(fy,ent) => `SELECT m.category, m.material_name, sum(sol.quantity) AS qty, sum(sol.line_amount) AS revenue, round(sum(sol.line_amount)/nullIf(sum(sol.quantity),0),2) AS asp FROM sales.sales_order_lines sol JOIN erp_core.materials m ON sol.material_id=m.material_id JOIN sales.sales_orders so ON sol.sales_order_id=so.sales_order_id WHERE so.order_status NOT IN ('CANCELLED') ${buildDateFilter(fy,"order_date","so")} ${buildEntityFilter(ent,"sol")} GROUP BY m.category,m.material_name ORDER BY revenue DESC LIMIT 50`,
  bookings_trend:     (fy,ent) => `SELECT e.entity_name, formatDateTime(so.order_date,'%b') AS month, count() AS orders, sum(so.total_amount) AS bookings FROM sales.sales_orders so JOIN erp_core.entities e ON so.entity_id=e.entity_id WHERE so.order_status NOT IN ('CANCELLED') ${buildDateFilter(fy,"order_date","so")} ${buildEntityFilter(ent,"so")} GROUP BY e.entity_name,month ORDER BY bookings DESC LIMIT 60`,
  aov_trend:          (fy,ent) => `SELECT e.entity_name, formatDateTime(so.order_date,'%b') AS month, count() AS orders, round(sum(so.total_amount)/count(),0) AS aov FROM sales.sales_orders so JOIN erp_core.entities e ON so.entity_id=e.entity_id WHERE so.order_status NOT IN ('CANCELLED') ${buildDateFilter(fy,"order_date","so")} ${buildEntityFilter(ent,"so")} GROUP BY e.entity_name,month ORDER BY aov DESC LIMIT 50`,
  new_customers:      (fy,ent) => `SELECT c.industry, formatDateTime(first_order,'%b') AS month, count() AS new_customers FROM (SELECT so.customer_id, min(so.order_date) AS first_order FROM sales.sales_orders so WHERE so.order_status NOT IN ('CANCELLED') ${buildDateFilter(fy,"order_date","so")} ${buildEntityFilter(ent,"so")} GROUP BY so.customer_id) t JOIN erp_core.customers c ON t.customer_id=c.customer_id GROUP BY c.industry,month ORDER BY new_customers DESC LIMIT 40`,
  asp:                (fy,ent) => `SELECT m.category, m.material_name, sum(sol.quantity) AS total_qty, round(sum(sol.line_amount)/nullIf(sum(sol.quantity),0),2) AS asp, count(DISTINCT so.customer_id) AS customers FROM sales.sales_order_lines sol JOIN erp_core.materials m ON sol.material_id=m.material_id JOIN sales.sales_orders so ON sol.sales_order_id=so.sales_order_id WHERE so.order_status NOT IN ('CANCELLED') ${buildDateFilter(fy,"order_date","so")} ${buildEntityFilter(ent,"sol")} GROUP BY m.category,m.material_name ORDER BY asp DESC LIMIT 50`,
  spend_trend:        (fy,ent) => `SELECT v.vendor_category, formatDateTime(po.po_date,'%b') AS month, count() AS po_count, sum(po.total_amount) AS spend FROM procurement.purchase_orders po JOIN erp_core.vendors v ON po.vendor_id=v.vendor_id WHERE 1=1 ${buildDateFilter(fy,"po_date","po")} ${buildEntityFilter(ent,"po")} GROUP BY v.vendor_category,month ORDER BY spend DESC LIMIT 60`,
  top_vendors:        (fy,ent) => `SELECT v.vendor_name, v.vendor_category, count() AS po_count, sum(po.total_amount) AS spend, round(avg(po.total_amount),0) AS avg_po FROM procurement.purchase_orders po JOIN erp_core.vendors v ON po.vendor_id=v.vendor_id WHERE 1=1 ${buildDateFilter(fy,"po_date","po")} ${buildEntityFilter(ent,"po")} GROUP BY v.vendor_name,v.vendor_category ORDER BY spend DESC LIMIT 50`,
  ppv:                (fy,ent) => `SELECT m.category, m.material_name, count() AS line_items, sum((pol.unit_price-m.standard_cost)*pol.quantity) AS ppv, round(avg(pol.unit_price),2) AS avg_actual, round(avg(m.standard_cost),2) AS std_cost FROM procurement.po_lines pol JOIN erp_core.materials m ON pol.material_id=m.material_id JOIN procurement.purchase_orders po ON pol.po_id=po.po_id WHERE 1=1 ${buildDateFilter(fy,"po_date","po")} ${buildEntityFilter(ent,"pol")} GROUP BY m.category,m.material_name ORDER BY abs(ppv) DESC LIMIT 50`,
  vendor_perf:        (fy,ent) => `SELECT v.vendor_name, v.vendor_category, count() AS deliveries, round(countIf(gr.receipt_date<=pol.delivery_date)*100.0/count(),1) AS on_time_pct, round(avg(dateDiff('day',po.po_date,gr.receipt_date)),1) AS avg_lead_days FROM procurement.purchase_orders po JOIN procurement.goods_receipts gr ON po.po_id=gr.po_id JOIN procurement.po_lines pol ON po.po_id=pol.po_id JOIN erp_core.vendors v ON po.vendor_id=v.vendor_id WHERE 1=1 ${buildDateFilter(fy,"po_date","po")} ${buildEntityFilter(ent,"po")} GROUP BY v.vendor_name,v.vendor_category ORDER BY on_time_pct ASC LIMIT 50`,
  vendor_category_spend:(fy,ent)=> `SELECT v.vendor_category, count(DISTINCT v.vendor_id) AS vendors, count() AS po_count, sum(po.total_amount) AS spend FROM procurement.purchase_orders po JOIN erp_core.vendors v ON po.vendor_id=v.vendor_id WHERE 1=1 ${buildDateFilter(fy,"po_date","po")} ${buildEntityFilter(ent,"po")} GROUP BY v.vendor_category ORDER BY spend DESC LIMIT 30`,
  active_vendors:     (fy,ent) => `SELECT toYear(po.po_date) AS yr, v.vendor_category, count(DISTINCT po.vendor_id) AS active_vendors, sum(po.total_amount) AS spend FROM procurement.purchase_orders po JOIN erp_core.vendors v ON po.vendor_id=v.vendor_id WHERE 1=1 ${buildEntityFilter(ent,"po")} GROUP BY yr,v.vendor_category ORDER BY yr DESC,spend DESC LIMIT 60`,
  headcount_vph:      (fy,ent) => `SELECT e.entity_name, emp.designation, emp.department_id, count() AS headcount FROM erp_core.employees emp JOIN erp_core.entities e ON emp.entity_id=e.entity_id WHERE emp.status='ACTIVE' ${buildEntityFilter(ent,"emp")} GROUP BY e.entity_name,emp.designation,emp.department_id ORDER BY headcount DESC LIMIT 60`,
  attrition:          (fy,ent) => `SELECT e.entity_name, emp.designation, formatDateTime(emp.exit_date,'%b') AS month, count() AS exits FROM erp_core.employees emp JOIN erp_core.entities e ON emp.entity_id=e.entity_id WHERE emp.status='INACTIVE' ${buildDateFilter(fy,"exit_date","emp")} ${buildEntityFilter(ent,"emp")} GROUP BY e.entity_name,emp.designation,month ORDER BY exits DESC LIMIT 50`,
  designation_mix:    (fy,ent) => `SELECT emp.designation, e.entity_name, count() AS headcount, round(avg(dateDiff('day',emp.hire_date,today())/365),1) AS avg_tenure_yrs FROM erp_core.employees emp JOIN erp_core.entities e ON emp.entity_id=e.entity_id WHERE emp.status='ACTIVE' ${buildEntityFilter(ent,"emp")} GROUP BY emp.designation,e.entity_name ORDER BY headcount DESC LIMIT 50`,
  payment_run:        (fy,ent) => `SELECT formatDateTime(payment_date,'%b') AS month, payment_method, count() AS payment_count, sum(amount) AS payment_value, round(avg(amount),0) AS avg_payment FROM finance.payments WHERE 1=1 ${buildDateFilter(fy,"payment_date")} ${buildEntityFilter(ent)} GROUP BY month,payment_method ORDER BY payment_value DESC LIMIT 50`,
  outstanding_invoices:(fy,ent)=> `SELECT v.vendor_name, v.vendor_category, count() AS open_invoices, sum(vi.invoice_amount) AS outstanding_value, round(avg(dateDiff('day',vi.invoice_date,today())),1) AS avg_days_open FROM finance.vendor_invoices vi LEFT JOIN finance.payments p ON vi.invoice_id=p.reference_invoice JOIN erp_core.vendors v ON vi.vendor_id=v.vendor_id WHERE p.payment_id IS NULL ${buildDateFilter(fy,"invoice_date","vi")} ${buildEntityFilter(ent,"vi")} GROUP BY v.vendor_name,v.vendor_category ORDER BY outstanding_value DESC LIMIT 50`,
  je_volume:          (fy,ent) => `SELECT toYear(posting_date) AS yr, account_type, count() AS entries, sum(amount_local) AS total_amount FROM finance.universal_journal WHERE 1=1 ${buildEntityFilter(ent)} GROUP BY yr,account_type ORDER BY yr DESC,entries DESC LIMIT 50`,
  top_gl_accounts:    (fy,ent) => `SELECT gl_account, account_type, count() AS entries, sum(CASE WHEN debit_credit_indicator='D' THEN amount_local ELSE 0 END) AS total_debit, sum(CASE WHEN debit_credit_indicator='C' THEN amount_local ELSE 0 END) AS total_credit FROM finance.universal_journal WHERE 1=1 ${buildDateFilter(fy,"posting_date")} ${buildEntityFilter(ent)} GROUP BY gl_account,account_type ORDER BY abs(total_debit-total_credit) DESC LIMIT 50`,
  dr_cr_balance:      (fy,ent) => `SELECT debit_credit_indicator, account_type, count() AS entries, sum(amount_local) AS total FROM finance.universal_journal WHERE 1=1 ${buildDateFilter(fy,"posting_date")} ${buildEntityFilter(ent)} GROUP BY debit_credit_indicator,account_type ORDER BY total DESC LIMIT 30`,
  collection_efficiency:(fy,ent)=>`SELECT formatDateTime(receipt_date,'%b') AS month, count(DISTINCT customer_id) AS customers, count() AS receipts, sum(amount) AS collections FROM finance.receipts WHERE 1=1 ${buildDateFilter(fy,"receipt_date")} ${buildEntityFilter(ent)} GROUP BY month ORDER BY collections DESC LIMIT 30`,
  backlog:            (fy,ent) => `SELECT e.entity_name, formatDateTime(so.order_date,'%b') AS month, count() AS backlog_orders, sum(so.total_amount) AS backlog_value FROM sales.sales_orders so JOIN erp_core.entities e ON so.entity_id=e.entity_id WHERE so.order_status='OPEN' ${buildDateFilter(fy,"order_date","so")} ${buildEntityFilter(ent,"so")} GROUP BY e.entity_name,month ORDER BY backlog_value DESC LIMIT 50`,
  delivery_trend:     (fy,ent) => `SELECT e.entity_name, formatDateTime(d.delivery_date,'%b') AS month, count() AS deliveries, round(avg(dateDiff('day',so.order_date,d.delivery_date)),1) AS avg_lead_days FROM sales.deliveries d JOIN sales.sales_orders so ON d.sales_order_id=so.sales_order_id JOIN erp_core.entities e ON d.entity_id=e.entity_id WHERE 1=1 ${buildDateFilter(fy,"delivery_date","d")} ${buildEntityFilter(ent,"d")} GROUP BY e.entity_name,month ORDER BY deliveries DESC LIMIT 50`,
  department_cost:    (fy,ent) => `SELECT department_id, gl_account, count() AS entries, sum(amount_local) AS cost FROM finance.universal_journal WHERE account_type='Expense' ${buildDateFilter(fy,"posting_date")} ${buildEntityFilter(ent)} GROUP BY department_id,gl_account ORDER BY cost DESC LIMIT 60`,
  department_headcount:(fy,ent)=> `SELECT department_id, designation, count() AS headcount, round(avg(dateDiff('day',hire_date,today())/365),1) AS avg_tenure FROM erp_core.employees WHERE status='ACTIVE' ${buildEntityFilter(ent)} GROUP BY department_id,designation ORDER BY headcount DESC LIMIT 60`,
  project_cost:       (fy,ent) => `SELECT profit_center, gl_account, count() AS entries, sum(amount_local) AS cost FROM finance.universal_journal WHERE account_type='Expense' ${buildDateFilter(fy,"posting_date")} ${buildEntityFilter(ent)} GROUP BY profit_center,gl_account ORDER BY cost DESC LIMIT 60`,
  project_revenue:    (fy,ent) => `SELECT profit_center, formatDateTime(order_date,'%b') AS month, count() AS orders, sum(total_amount) AS revenue FROM sales.sales_orders WHERE order_status NOT IN ('CANCELLED') ${buildDateFilter(fy,"order_date")} ${buildEntityFilter(ent)} GROUP BY profit_center,month ORDER BY revenue DESC LIMIT 60`,
};


/* ═══════════════════════════════════════════════
   DRILL MODAL — fires live grouped query
═══════════════════════════════════════════════ */
const DrillModal = ({ chart, fy, entity, onClose }) => {
  const [drillData, setDrillData] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);

  useEffect(() => {
    const qFn = DRILL_QUERIES[chart.key];
    if (!qFn) { setLoading(false); setError("No drill query defined."); return; }
    const fyObj = FIN_YEARS.find(f => f.id === fy);
    const q = qFn(fyObj, entity);
    (async () => {
      try {
        const [creds, url] = Fetchexecuteclquery();
        const res = await axios.post(url, { query: q }, creds);
        let data = [];
        if (Array.isArray(res.data))              data = res.data;
        else if (Array.isArray(res.data?.data))   data = res.data.data;
        else if (Array.isArray(res.data?.result)) data = res.data.result;
        setDrillData(data);
      } catch(e) { setError("Failed to load drilldown."); }
      finally    { setLoading(false); }
    })();
  }, [chart.key, fy, entity]);

  const doExport = () => {
    if (!drillData.length) return;
    const keys = Object.keys(drillData[0]);
    const csv = [keys.join(","), ...drillData.map(r=>keys.map(k=>`"${r[k]}"`).join(","))].join("\n");
    const a = document.createElement("a");
    a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    a.download = `${chart.key}_drill.csv`; a.click();
  };

  const fmtCell = (k, v) => {
    if (v == null) return "—";
    if (typeof v === "number") {
      if (Math.abs(v) > 1e4) return fmt.currency(v);
      if (k.includes("rate") || k.includes("pct")) return fmt.percent(v);
      if (k.includes("day") || k.includes("tenure")) return `${v}d`;
      return fmt.number(v);
    }
    return String(v);
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(15,22,40,0.65)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:C.surface, borderRadius:14, width:"92vw", maxWidth:1000, maxHeight:"88vh", overflow:"hidden", display:"flex", flexDirection:"column", boxShadow:"0 24px 80px rgba(0,0,0,0.3)" }}>
        <div style={{ padding:"12px 18px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"space-between", background:C.surfaceAlt, flexShrink:0 }}>
          <div>
            <div style={{ fontSize:9, color:C.textMut, fontWeight:600, letterSpacing:"0.1em", textTransform:"uppercase" }}>{chart.category} · Grouped Drilldown</div>
            <div style={{ fontSize:14, fontWeight:700, color:C.text, fontFamily:C.head }}>{chart.label}</div>
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            {!loading && !error && <span style={{ fontSize:10, color:C.textMut }}>{drillData.length} groups</span>}
            <button onClick={doExport} disabled={!drillData.length} style={{ background:C.greenSoft, border:`1px solid ${C.green}`, borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:11, color:C.green, fontWeight:600, opacity:drillData.length?1:0.4 }}>↓ CSV</button>
            <button onClick={onClose} style={{ background:"none", border:`1px solid ${C.border}`, borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:13, color:C.textSub }}>×</button>
          </div>
        </div>
        <div style={{ flex:1, overflowY:"auto", padding:"12px 18px" }}>
          {loading && <div style={{ display:"flex", flexDirection:"column", gap:8 }}>{[...Array(8)].map((_,i)=><Shimmer key={i} h={30} r={5}/>)}</div>}
          {error   && <div style={{ padding:"40px 0", textAlign:"center", color:C.red, fontSize:13 }}>{error}</div>}
          {!loading && !error && drillData.length===0 && <div style={{ padding:"40px 0", textAlign:"center", color:C.textMut, fontSize:13 }}>No data for current filters.</div>}
          {!loading && !error && drillData.length>0 && (
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, fontFamily:C.sans }}>
                <thead>
                  <tr style={{ background:C.surfaceAlt }}>
                    {Object.keys(drillData[0]).map(k=>(
                      <th key={k} style={{ padding:"8px 12px", textAlign:"left", fontWeight:700, fontSize:10, color:C.textSub, letterSpacing:"0.07em", textTransform:"uppercase", borderBottom:`2px solid ${C.border}`, whiteSpace:"nowrap", position:"sticky", top:0, background:C.surfaceAlt }}>
                        {k.replace(/_/g," ")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {drillData.map((row,i)=>(
                    <tr key={i} style={{ borderBottom:`1px solid ${C.border}` }}
                      onMouseEnter={e=>e.currentTarget.style.background=C.surfaceAlt}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      {Object.entries(row).map(([k,v])=>(
                        <td key={k} style={{ padding:"6px 12px", color:typeof v==="number"?C.blue:C.textMid, fontFamily:typeof v==="number"?C.mono:C.sans, fontWeight:typeof v==="number"?600:400, whiteSpace:"nowrap" }}>
                          {fmtCell(k,v)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   CHART RENDERER — compact height 160px
 — compact height 160px
═══════════════════════════════════════════════ */
const ChartRenderer = ({ cfg, data, loading }) => {
  if (loading) return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
      <Shimmer h={18} /><Shimmer h="100%" r={8} /><Shimmer h={12} w="50%" />
    </div>
  );
  if (!data?.length) return (
    <div style={{
      flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
      color: C.textMut, fontSize: 12, fontFamily: C.sans,
      border: `1.5px dashed ${C.border}`, borderRadius: 8,
    }}>No data available</div>
  );

  const { chartType, xKey, yKey, yKeys, fmt: fmtType } = cfg;

  const axisProps = { tick: { fill: C.textSub, fontSize: 10, fontFamily: C.sans }, axisLine: false, tickLine: false };
  const gridProps = { strokeDasharray: "3 3", stroke: C.border };

  if (chartType === "area") return (
    <div style={{ flex: 1, minHeight: 0 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={`g_${cfg.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={C.navyMid} stopOpacity={0.2} />
              <stop offset="95%" stopColor={C.navyMid} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid {...gridProps} />
          <XAxis dataKey={xKey} {...axisProps} />
          <YAxis {...axisProps} tickFormatter={v => fmtVal(v, fmtType)} width={62} />
          <Tooltip content={<CustomTT fmtType={fmtType} />} />
          <Area type="monotone" dataKey={yKey} stroke={C.navyMid} strokeWidth={2} fill={`url(#g_${cfg.key})`} dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );

  if (chartType === "line") return (
    <div style={{ flex: 1, minHeight: 0 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <CartesianGrid {...gridProps} />
          <XAxis dataKey={xKey} {...axisProps} />
          <YAxis {...axisProps} tickFormatter={v => fmtVal(v, fmtType)} width={62} />
          <Tooltip content={<CustomTT fmtType={fmtType} />} />
          <Line type="monotone" dataKey={yKey} stroke={C.blue} strokeWidth={2} dot={{ r: 2.5, fill: C.blue }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );

  if (chartType === "bar") return (
    <div style={{ flex: 1, minHeight: 0 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} barSize={14} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <CartesianGrid {...gridProps} />
          <XAxis dataKey={xKey} {...axisProps} tick={{ ...axisProps.tick, fontSize: 9 }} />
          <YAxis {...axisProps} tickFormatter={v => fmtVal(v, fmtType)} width={62} />
          <Tooltip content={<CustomTT fmtType={fmtType} />} />
          <Bar dataKey={yKey} fill={C.navyMid} radius={[3, 3, 0, 0]}>
            {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

if (chartType === "pie" || chartType === "pie_ar" || chartType === "pie_ap") {
  const pKey = yKey || "amount";
  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius="35%"
              outerRadius="65%"
              dataKey={pKey}
              paddingAngle={3}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke={C.surface} strokeWidth={2} />
              ))}
            </Pie>
            <Tooltip
              formatter={v => fmtVal(v, fmtType)}
              contentStyle={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 11 }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 16px", paddingBottom: 4, flexShrink: 0 }}>
        {data.map((d, i) => {
          const name = d[xKey] || `Item ${i + 1}`;
          const val = d[pKey];
          const total = data.reduce((s, r) => s + (r[pKey] || 0), 0);
          return (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 120 }}>
              <div style={{ width: 9, height: 9, borderRadius: 2, background: CHART_COLORS[i % CHART_COLORS.length], flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: C.textMid, fontFamily: C.sans, flex: 1 }}>{name}</span>
              <span style={{ fontSize: 11, fontFamily: C.mono, color: C.navyMid, fontWeight: 700 }}>
                {total ? `${((val / total) * 100).toFixed(1)}%` : "—"}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

  if (chartType === "composed") {
    const keys = yKeys || [yKey];
    const colors = [C.navyMid, C.amber, C.teal, C.purple];

    const allVals0 = data.map(r => Math.abs(Number(r[keys[0]]) || 0)).filter(v => v > 0);
    const allVals1 = keys[1] ? data.map(r => Math.abs(Number(r[keys[1]]) || 0)).filter(v => v > 0) : [];
    const med0 = allVals0.length ? allVals0.reduce((a, b) => a + b, 0) / allVals0.length : 0;
    const med1 = allVals1.length ? allVals1.reduce((a, b) => a + b, 0) / allVals1.length : 0;
    const dualAxis = keys.length > 1 && med0 > 0 && med1 > 0 && (med0 / med1 > 20 || med1 / med0 > 20);
    const fmtLeft = fmtType;
    const fmtRight = fmtType === "mixed" ? "number" : fmtType;

    return (
      <div style={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 4, right: dualAxis ? 52 : 4, left: 0, bottom: 0 }}>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey={xKey} {...axisProps} />
            <YAxis yAxisId="left" {...axisProps} tickFormatter={v => fmtVal(v, fmtLeft)} width={62} />
            {dualAxis && (
              <YAxis yAxisId="right" orientation="right" {...axisProps} tickFormatter={v => fmtVal(v, fmtRight)} width={50} />
            )}
            <Tooltip content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              return (
                <div style={{ background: C.surface, border: `1px solid ${C.borderMed}`, borderRadius: 8, padding: "7px 10px", fontSize: 11, fontFamily: C.sans }}>
                  <p style={{ color: C.textSub, marginBottom: 4, fontSize: 10 }}>{label}</p>
                  {payload.map((p, i) => (
                    <p key={i} style={{ color: p.color || C.text, fontFamily: C.mono, fontWeight: 600, marginBottom: 1, fontSize: 11 }}>
                      <span style={{ color: C.textSub, fontWeight: 400, fontFamily: C.sans }}>{p.name}: </span>
                      {fmtVal(p.value, dualAxis && i > 0 ? fmtRight : fmtLeft)}
                    </p>
                  ))}
                </div>
              );
            }} />
            <Legend wrapperStyle={{ fontSize: 10, fontFamily: C.sans }} />
            {keys.map((k, i) => {
              const yId = dualAxis && i > 0 ? "right" : "left";
              return i === 0
                ? <Bar key={k} yAxisId={yId} dataKey={k} fill={colors[i]} barSize={14} radius={[3, 3, 0, 0]} />
                : <Line key={k} yAxisId={yId} type="monotone" dataKey={k} stroke={colors[i]} strokeWidth={2} dot={false} />;
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return null;
};

/* ═══════════════════════════════════════════════
   EXPORT CSV HELPER
═══════════════════════════════════════════════ */
const exportCSV = (data, filename) => {
  if (!data?.length) return;
  const keys = Object.keys(data[0]);
  const csv = [keys.join(","), ...data.map(r=>keys.map(k=>`"${r[k]}"`).join(","))].join("\n");
  const a = document.createElement("a"); a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv);
  a.download=filename+".csv"; a.click();
};

/* ═══════════════════════════════════════════════
   MAIN DASHBOARD
═══════════════════════════════════════════════ */
export default function EPMDashboard() {
  const [role,        setRole]        = useState("CEO");
  const [fy,          setFy]          = useState("FY2025");
  const [entity,      setEntity]      = useState("ALL");
  const [chartData,   setChartData]   = useState({});
  const [loading,     setLoading]     = useState({});
  const [drill,       setDrill]       = useState(null);
  const [roleOpen,    setRoleOpen]    = useState(false);
  const [fyOpen,      setFyOpen]      = useState(false);
  const [entityOpen,  setEntityOpen]  = useState(false);

const loadData = useCallback((roleId, fyId, entityId) => {
  const fyObj = FIN_YEARS.find(f => f.id === fyId);
  const qdefs = ROLE_QUERIES[roleId]?.(fyObj, entityId) || [];

  setChartData({});
  const newLoading = {};
  qdefs.forEach(q => { newLoading[q.key] = true; });
  setLoading(newLoading);

  qdefs.forEach((q) => {
    (async () => {
      try {
        const [creds, url] = Fetchexecuteclquery();
        const res = await axios.post(url, { query: q.query }, creds);

        let data = [];
        if (Array.isArray(res.data))              data = res.data;
        else if (Array.isArray(res.data?.data))   data = res.data.data;
        else if (Array.isArray(res.data?.result)) data = res.data.result;

        // Aging bucket transformation
        if (q.key === "ar_aging" || q.key === "ap_aging") {
          const row = data[0] || {};
          data = [
            { bucket: "0–30 days",  amount: Number(row.amt_0_30)    || 0 },
            { bucket: "31–60 days", amount: Number(row.amt_31_60)   || 0 },
            { bucket: "61–90 days", amount: Number(row.amt_61_90)   || 0 },
            { bucket: "> 90 days",  amount: Number(row.amt_over_90) || 0 },
          ];
        }

        // Coerce all numeric-looking strings from ClickHouse to JS numbers
        // (ClickHouse returns numbers as strings — causes NaN% in pies and flat lines in charts)
        data = data.map(r =>
          Object.fromEntries(
            Object.entries(r).map(([k, v]) => [
              k,
              v !== null && v !== "" && !isNaN(Number(v)) ? Number(v) : v,
            ])
          )
        );

        // Parse full date strings like "2024-04-01" into "Apr '24"
        // Must run AFTER numeric coercion so month strings aren't converted to NaN
        if (q.xKey === "month") {
          data = data.map(r => ({
            ...r,
            month: parseMonthLabel(String(r.month)),
          }));
        }

        setChartData(prev => ({ ...prev, [q.key]: data }));
      } catch (err) {
        console.error("Query failed:", q.key, err);
        setChartData(prev => ({ ...prev, [q.key]: [] }));
      } finally {
        setLoading(prev => ({ ...prev, [q.key]: false }));
      }
    })();
  });
}, []);

  useEffect(() => { loadData(role, fy, entity); }, [role, fy, entity]);

  const roleInfo  = ROLES.find(r => r.id === role);
  const qdefs     = ROLE_QUERIES[role]?.(FIN_YEARS.find(f=>f.id===fy), entity) || [];
  const summaryKpis = SUMMARY_KPIS[role]?.(chartData) || [];

  /* ── DROPDOWN COMPONENT ── */
  const Dropdown = ({ open, setOpen, value, options, onChange, label }) => (
    <div style={{ position:"relative" }}>
      <button
        onClick={e => { e.stopPropagation(); setOpen(o=>!o); }}
        style={{ display:"flex", alignItems:"center", gap:6, background:C.surface, border:`1px solid ${C.border}`, borderRadius:6, padding:"5px 10px", cursor:"pointer", fontFamily:C.sans, fontSize:12, color:C.text, fontWeight:600, whiteSpace:"nowrap", boxShadow:"0 1px 3px rgba(0,0,0,0.05)" }}>
        <span style={{ fontSize:10, color:C.textMut, fontWeight:400 }}>{label}</span>
        <span>{options.find(o=>o.id===value)?.icon&&<span style={{ marginRight:3 }}>{options.find(o=>o.id===value).icon}</span>}{options.find(o=>o.id===value)?.label}</span>
        <span style={{ color:C.textMut, fontSize:9, transform:open?"rotate(180deg)":"none", transition:"transform 0.2s" }}>▾</span>
      </button>
      {open && (
        <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, background:C.surface, border:`1px solid ${C.border}`, borderRadius:8, boxShadow:"0 8px 24px rgba(0,0,0,0.12)", zIndex:200, minWidth:180, overflow:"hidden" }}>
          {options.map(opt=>(
            <div key={opt.id} onClick={()=>{ onChange(opt.id); setOpen(false); }}
              style={{ padding:"7px 12px", cursor:"pointer", fontSize:12, fontFamily:C.sans, color:opt.id===value?C.blue:C.text, fontWeight:opt.id===value?700:400, background:opt.id===value?C.blueSoft:"transparent", display:"flex", alignItems:"center", gap:7 }}
              onMouseEnter={e=>{ if(opt.id!==value) e.currentTarget.style.background=C.surfaceAlt; }}
              onMouseLeave={e=>{ e.currentTarget.style.background=opt.id===value?C.blueSoft:"transparent"; }}>
              {opt.icon&&<span style={{ color:opt.color||C.blue, fontSize:13 }}>{opt.icon}</span>}
              {opt.label}
              {opt.id===value&&<span style={{ marginLeft:"auto", color:C.blue }}>✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Close dropdowns on outside click
  useEffect(()=>{
    const close = () => { setRoleOpen(false); setFyOpen(false); setEntityOpen(false); };
    document.addEventListener("click", close);
    return ()=>document.removeEventListener("click", close);
  },[]);

  return (
    <div style={{ height:"127vh", overflow:"hidden", background:C.bg, fontFamily:C.sans, display:"flex", flexDirection:"column" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;600&display=swap');
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes fadeUp  { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:none} }
        * { box-sizing:border-box; margin:0; padding:0; }
        button:focus { outline:none; }
        ::-webkit-scrollbar { width:4px; height:4px; }
        ::-webkit-scrollbar-track { background:${C.bg}; }
        ::-webkit-scrollbar-thumb { background:${C.borderMed}; border-radius:3px; }
      `}</style>

      {/* ── HEADER ── */}
      <div style={{ background:C.navy, padding:"0 20px", boxShadow:"0 2px 10px rgba(0,0,0,0.15)", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", height:40 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:28, height:28, borderRadius:7, background:"linear-gradient(135deg,#3B82F6,#1D4ED8)", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 2px 6px rgba(59,130,246,0.4)" }}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="1" y="8" width="3" height="7" rx="1" fill="white" opacity="0.7"/><rect x="6" y="4" width="3" height="11" rx="1" fill="white"/><rect x="11" y="1" width="3" height="14" rx="1" fill="white" opacity="0.85"/></svg>
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <div style={{ background:"rgba(255,255,255,0.1)", borderRadius:5, padding:"3px 8px", fontSize:10, color:"rgba(255,255,255,0.65)", fontFamily:C.sans }}>
              {new Date().toLocaleDateString("en-IN",{ day:"numeric", month:"short", year:"numeric" })}
            </div>
            <div style={{ background:"rgba(16,185,129,0.2)", border:"1px solid rgba(16,185,129,0.4)", borderRadius:5, padding:"3px 8px", fontSize:10, color:"#6EE7B7", fontFamily:C.sans, display:"flex", alignItems:"center", gap:4 }}>
              <span style={{ width:5, height:5, background:"#10B981", borderRadius:"50%", display:"inline-block" }}/>Live
            </div>
          </div>
        </div>
      </div>

      {/* ── FILTER BAR ── */}
      <div style={{ background:C.surface, borderBottom:`1px solid ${C.border}`, padding:"6px 20px", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
          <span style={{ fontSize:10, color:C.textMut, fontWeight:600, letterSpacing:"0.08em", textTransform:"uppercase" }}>Filters:</span>
          <div onClick={e=>e.stopPropagation()}><Dropdown open={roleOpen}   setOpen={setRoleOpen}   value={role}   options={ROLES}      onChange={setRole}   label="Role"   /></div>
          <div onClick={e=>e.stopPropagation()}><Dropdown open={fyOpen}     setOpen={setFyOpen}     value={fy}     options={FIN_YEARS}  onChange={setFy}     label="FY"     /></div>
          <div onClick={e=>e.stopPropagation()}><Dropdown open={entityOpen} setOpen={setEntityOpen} value={entity} options={ENTITIES}   onChange={setEntity} label="Entity" /></div>
          <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:6, background:C.blueSoft, border:`1px solid ${C.blueLight}`, borderRadius:6, padding:"4px 10px" }}>
            <span style={{ fontSize:13, color:roleInfo?.color }}>{roleInfo?.icon}</span>
            <span style={{ fontSize:11, fontWeight:700, color:C.blue, fontFamily:C.sans }}>{roleInfo?.label}</span>
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT — fills remaining height, no scroll ── */}
      <div style={{ flex:1, minHeight:0, padding:"8px 16px", display:"flex", flexDirection:"column", gap:8, overflow:"hidden" }}>

{/* KPI Summary Row */}
<div style={{
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: 8,
  flexShrink: 0,
  width: "100%",
  height: 80,
}}>
  {summaryKpis.map((k, i) => (
    <div key={i} style={{ animation: `fadeUp 0.3s ease ${i * 0.05}s both`, minWidth: 0, height: "100%" }}>
      <KpiTile label={k.label} value={k.value} color={k.color} loading={Object.values(loading).some(Boolean)} />
    </div>
  ))}
</div>
        {/* Charts Grid — 3 columns, fills remaining space */}
        <div style={{ flex:1, minHeight:0, display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, gridAutoRows:"1fr" }}>
  {qdefs.filter(q=>q.chartType!=="kpi"&&q.chartType!=="kpi_only").map((q,i)=>(
    <div key={q.key} style={{ animation:`fadeUp 0.4s ease ${0.05+i*0.04}s both`, display:"flex", flexDirection:"column", minHeight:0, height:"100%" }}>
              <ChartCard
                title={q.label}
                category={q.category}
                query={q.query}
                onExport={()=>exportCSV(chartData[q.key]||[], q.key)}
                onDrilldown={()=>setDrill(q)}
              >
                <ChartRenderer cfg={q} data={chartData[q.key]} loading={loading[q.key]}/>
              </ChartCard>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ flexShrink:0, padding:"4px 0", borderTop:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ fontSize:10, color:C.textMut, fontFamily:C.sans }}>EPM Intelligence · {qdefs.length} KPIs · {roleInfo?.label}</span>
          <span style={{ fontSize:10, color:C.textMut, fontFamily:C.mono }}>ClickHouse · India Fiscal Year (Apr–Mar)</span>
        </div>
      </div>

      {/* Drill Modal */}
      {drill && <DrillModal chart={drill} fy={fy} entity={entity} onClose={()=>setDrill(null)}/>}
    </div>
  );
}